import React, {useMemo, useState} from 'react';
import './i18n/config'; // Importa la configurazione i18n
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router-dom';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {Box, CssBaseline, Theme, Typography} from '@mui/material';
import {AuthProvider, useAuth} from './contexts/AuthContext';
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PersonalDataPage from './pages/PersonalDataPage';
import CookieConsent from 'react-cookie-consent';
import GenericListPage from "./pages/GenericListPage";
import {SectionTypes} from "./utils/enums";
import {Log} from "./utils/Log";
import {LocaleProvider} from "./contexts/LocaleContext";
import {GoogleOAuthProvider} from "@react-oauth/google";
import UserProfile from "./pages/UserProfile";
import Subscription from "./pages/Subscription";
import ShareDashboard from "./pages/Sharedashboard";
import { UpdatePrompt } from './components/UpdatePrompt';

let themeApp = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
    },
});

const onChangeTheme = (theme: Theme) => {
    Log.writeLogDebug(themeApp);
    themeApp = theme;
    Log.writeLogDebug(themeApp);
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {isAuthenticated, isLoading} = useAuth();

    if (isLoading) {
        return <Box>Caricamento...</Box>;
    }

    return isAuthenticated ? <>{children}</> : <Navigate to="/login"/>;
};

interface AppContentProps {
    onToggleDarkMode: () => void;
}

const AppContent: React.FC<AppContentProps> = ({onToggleDarkMode}) => {
    const {isAuthenticated} = useAuth();

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
            <Navigation onToggleDarkMode={onToggleDarkMode}/>
            <Box component="main" sx={{flexGrow: 1}}>
                <Routes>
                    <Route path="/" element={<Navigate to="/dashboard"/>}/>
                    <Route path="/home" element={<HomePage/>}/>
                    <Route path="/login" element={<LoginPage/>}/>
                    <Route path="/register" element={<RegisterPage/>}/>
                    <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>
                    <Route path="/reset-password" element={<ResetPasswordPage/>}/>
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <DashboardPage/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/personal-data"
                        element={
                            <ProtectedRoute>
                                <PersonalDataPage/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/education"
                        element={
                            <ProtectedRoute>
                                <GenericListPage sectionType={SectionTypes.EDUCATION}/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/experience"
                        element={
                            <ProtectedRoute>
                                <GenericListPage sectionType={SectionTypes.EXPERIENCE}/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/skills"
                        element={
                            <ProtectedRoute>
                                <GenericListPage sectionType={SectionTypes.SKILL}/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/certifications"
                        element={
                            <ProtectedRoute>
                                <GenericListPage sectionType={SectionTypes.CERTIFICATION}/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/projects"
                        element={
                            <ProtectedRoute>
                                <GenericListPage sectionType={SectionTypes.PROJECT}/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/languages"
                        element={
                            <ProtectedRoute>
                                <GenericListPage sectionType={SectionTypes.LANGUAGE}/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/shares"
                        element={
                            <ProtectedRoute>
                                {/*<Box p={4}>
                                    <Typography variant="h4">Condivisioni</Typography>
                                    <Typography>Pagina in costruzione...</Typography>
                                </Box>*/}
                                <ShareDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute>
                                <Box p={4}>
                                    <UserProfile />
                                </Box>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/subscription"
                        element={
                            <ProtectedRoute>
                                <Box p={4}>
                                    <Subscription />
                                </Box>
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </Box>

            <UpdatePrompt />

            {/* Cookie Consent */}
            <CookieConsent
                location="bottom"
                buttonText="Accetto"
                cookieName="cv-portal-cookies"
                style={{background: '#2B373B'}}
                buttonStyle={{color: '#4e503b', fontSize: '13px'}}
                expires={150}
            >
                Questo sito utilizza i cookie per migliorare la tua esperienza. Continuando a navigare, accetti
                l'utilizzo dei cookie.
            </CookieConsent>
        </Box>
    );
};

const App: React.FC = () => {

    const [darkMode, setDarkMode] = useState(() => {
        // Opzionale: recupera la preferenza salvata
        const saved = localStorage.getItem('darkMode');
        return saved ? JSON.parse(saved) : false;
    });

    const themeApp = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: darkMode ? 'dark' : 'light',
                }
            }),
        [darkMode]
    );

    const handleToggleDarkMode = () => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        // Opzionale: salva la preferenza
        localStorage.setItem('darkMode', JSON.stringify(newMode));
    };

    return (
        <GoogleOAuthProvider clientId="918112587912-ho9kr3t9nik2bn5c38ci8ed6sucuot7s.apps.googleusercontent.com">
            <ThemeProvider theme={themeApp}>
                <LocaleProvider>
                    <CssBaseline/>
                    <AuthProvider>
                        <Router>
                            <AppContent onToggleDarkMode={handleToggleDarkMode}/>
                        </Router>
                    </AuthProvider>
                </LocaleProvider>
            </ThemeProvider>
        </GoogleOAuthProvider>
    );
};

export default App;
